import React from 'react';
import {
    AlertCircle,
    AlertTriangle,
    Ban,
    CheckCircle,
    Clock,
} from 'lucide-react';
import IconButton from '../ui/IconButton';
import Popover from '../ui/Popover';
import Tooltip from '../ui/Tooltip';
import { cx } from '../ui/classNames';
import {
    MARKET_HEALTH_LABELS,
    marketHealthReasonLabel,
    marketHealthStatus,
} from '../../utils/marketHealth';

// MUI's success, warning and error mains and its disabled text, the colours
// the icons had; the theme's ink on hover, as the icon button's own hover.
// The button sits on the cell's text line as MUI's do (vertically centred).
const TONE_CLASS_NAMES = Object.freeze({
    success: '!text-[#2e7d32] dark:!text-[#66bb6a]',
    warning: '!text-[#ed6c02] dark:!text-[#ffa726]',
    error: '!text-[#d32f2f] dark:!text-[#f44336]',
    disabled: '!text-black/[0.38] dark:!text-white/50',
});
const BUTTON_CLASS_NAME = 'hover:!text-ui-ink dark:hover:!text-ui-ink';
// MUI's small icon size, which scales with the compact type.
const ICON_CLASS_NAME = '[[data-compact]_&]:size-[17.14px]';

// The Watchlist shows health as an icon. Each status differs in shape as well
// as color, so it reads without color; `shape` names the icon (data-icon).
const PRESENTATION = {
    healthy: { tone: 'success', Icon: CheckCircle, shape: 'check-circle', description: 'The most recent market health check passed.' },
    concern: { tone: 'warning', Icon: AlertTriangle, shape: 'warning-triangle', description: 'The latest market health check found a problem.' },
    sick: { tone: 'error', Icon: AlertCircle, shape: 'alert-circle', description: 'Market health checks have found repeated problems.' },
    dead: { tone: 'error', Icon: Ban, shape: 'blocked', description: 'Persistent loss of market-data coverage has been confirmed. Removal is subject to portfolio-impact checks.' },
    unknown: { tone: 'disabled', Icon: Clock, shape: 'clock', description: 'Current market health has not been confirmed.' },
};

// MUI's body2, subtitle2 and caption type in the app theme (smaller in
// compact mode), as the tooltip and the details panel set their text.
const BODY_TYPE_CLASS_NAME = 'm-0 text-[14px] leading-[1.43] tracking-normal [[data-compact]_&]:text-[12px]';
const BODY_CLASS_NAME = cx(BODY_TYPE_CLASS_NAME, 'font-normal');
const TITLE_CLASS_NAME = 'm-0 mb-2 text-[14px] font-medium leading-[1.57] tracking-normal [[data-compact]_&]:text-[12px]';
const CAPTION_CLASS_NAME = 'm-0 block text-[12px] font-normal leading-[1.66] tracking-normal '
    + '[[data-compact]_&]:text-[10.29px]';

function checkedAt(value) {
    const date = value ? new Date(value) : null;
    return date && Number.isFinite(date.getTime()) ? date.toLocaleString() : null;
}

export default function WatchlistMarketHealthChip({ health, canRemove = false }) {
    const status = marketHealthStatus(health);
    const label = MARKET_HEALTH_LABELS[status];
    const {
        tone,
        Icon,
        shape,
        description,
    } = PRESENTATION[status];
    const reasons = [...new Set((Array.isArray(health?.reason_codes) ? health.reason_codes : [])
        .map(marketHealthReasonLabel).filter(Boolean))];
    const count = Number(health?.consecutive_failures);
    const lastChecked = checkedAt(health?.last_checked_at);
    const lastSuccess = checkedAt(health?.last_success_at);
    const firstFailure = checkedAt(health?.first_failure_at);
    const reasonLines = reasons.map((reason) => (
        <p key={reason} className={cx(BODY_CLASS_NAME, 'mt-1')}>{reason}</p>
    ));
    // Hover says what the icon means; a click adds the evidence.
    const meaning = (
        <div className="max-w-[300px]">
            <p className={cx(BODY_TYPE_CLASS_NAME, 'font-semibold')}>Market health: {label}</p>
            <p className={BODY_CLASS_NAME}>{description}</p>
            {reasonLines}
        </div>
    );

    return (
        <Popover
            placement="bottom-start"
            offset={0}
            modal
            aria-label={`Market health: ${label}`}
            className="p-4"
            trigger={(
                <Tooltip content={meaning}>
                    <IconButton
                        label={`Market health: ${label}`}
                        onClick={(event) => event.stopPropagation()}
                        className={cx(TONE_CLASS_NAMES[tone], BUTTON_CLASS_NAME)}
                    >
                        <Icon
                            aria-hidden="true"
                            data-testid="market-health-icon"
                            data-icon={shape}
                            size={20}
                            className={ICON_CLASS_NAME}
                        />
                    </IconButton>
                </Tooltip>
            )}
        >
            <p className={TITLE_CLASS_NAME}>Market health: {label}</p>
            <div className="max-w-[340px]">
                <p className={BODY_CLASS_NAME}>{description}</p>
                {reasonLines}
                {Number.isSafeInteger(count) && count > 0 && (
                    <p className={cx(BODY_CLASS_NAME, 'mt-1')}>
                        {count} consecutive failed {count === 1 ? 'check' : 'checks'}
                    </p>
                )}
                {lastChecked && <span className={cx(CAPTION_CLASS_NAME, 'mt-1')}>Last checked: {lastChecked}</span>}
                {lastSuccess && <span className={CAPTION_CLASS_NAME}>Last passed: {lastSuccess}</span>}
                {firstFailure && <span className={CAPTION_CLASS_NAME}>First failed check: {firstFailure}</span>}
                {canRemove && ['concern', 'sick', 'dead'].includes(status) && (
                    <p className={cx(BODY_CLASS_NAME, 'mt-2')}>
                        You can use this row’s Remove action and save your changes to review removal now.
                    </p>
                )}
            </div>
        </Popover>
    );
}
